import React, { useState } from 'react';
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer,
  ReferenceLine
} from 'recharts';
import './Milestones.css';

const Milestones = ({ milestones }) => {
  const [formData, setFormData] = useState({
    initialCapital: 1000,
    monthlyContribution: 100,
    interestRate: 0.8,
  });
  
  const [targetValues, setTargetValues] = useState([
    { name: 'Primeiro Carro', value: 30000, enabled: true },
    { name: 'Entrada Apartamento', value: 100000, enabled: true },
    { name: 'Independência Financeira', value: 1000000, enabled: true },
  ]);
  
  const [milestonesResults, setMilestonesResults] = useState([]);
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: parseFloat(value) || 0,
    });
  };
  
  const handleTargetChange = (index, e) => {
    const { name, value, type, checked } = e.target;
    const updatedTargets = [...targetValues];
    updatedTargets[index] = {
      ...updatedTargets[index],
      [name]: type === 'checkbox' ? checked : (parseFloat(value) || value),
    };
    setTargetValues(updatedTargets);
  };
  
  const calculateTimeToTarget = (target) => {
    const monthlyRate = formData.interestRate / 100;
    let amount = formData.initialCapital;
    let months = 0;
    
    while (amount < target && months < 1200) { // Limite de 100 anos
      amount = amount * (1 + monthlyRate) + formData.monthlyContribution;
      months++;
    }
    
    return {
      months,
      years: months / 12,
      amount,
    };
  };
  
  const calculateMilestones = () => {
    const enabledTargets = targetValues.filter(target => target.enabled);
    
    const results = enabledTargets.map(target => {
      const result = calculateTimeToTarget(target.value);
      
      return {
        name: target.name,
        targetValue: target.value,
        timeInMonths: result.months,
        timeInYears: result.years,
        finalAmount: result.amount,
      };
    });
    
    // Sort by time to reach
    results.sort((a, b) => a.timeInMonths - b.timeInMonths);
    
    // Generate data for chart
    const maxMonths = results[results.length - 1]?.timeInMonths || 120;
    const chartData = [];
    
    let amount = formData.initialCapital;
    for (let month = 0; month <= maxMonths; month++) {
      if (month > 0) {
        amount = amount * (1 + formData.interestRate / 100) + formData.monthlyContribution;
      }
      
      chartData.push({
        month,
        amount,
      });
    }
    
    setMilestonesResults({
      targets: results,
      chartData,
    });
  };
  
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };
  
  const formatDate = (monthsFromNow) => {
    const date = new Date();
    date.setMonth(date.getMonth() + monthsFromNow);
    
    return date.toLocaleDateString('pt-BR', {
      year: 'numeric',
      month: 'long',
    });
  };
  
  // Icons para os objetivos financeiros
  const getTargetIcon = (targetName) => {
    const name = targetName.toLowerCase();
    let icon = '🎯';
    let label = 'Alvo';
    
    if (name.includes('carro')) {
      icon = '🚗';
      label = 'Carro';
    } else if (name.includes('apartamento') || name.includes('casa')) {
      icon = '🏠';
      label = 'Imóvel';
    } else if (name.includes('indepen')) {
      icon = '💰';
      label = 'Independência Financeira';
    } else if (name.includes('educação') || name.includes('estudo')) {
      icon = '🎓';
      label = 'Educação';
    } else if (name.includes('viagem') || name.includes('férias')) {
      icon = '✈️';
      label = 'Viagem';
    } else if (name.includes('aposentadoria')) {
      icon = '👴';
      label = 'Aposentadoria';
    }
    
    return <span role="img" aria-label={label}>{icon}</span>;
  };
  
  return (
    <div className="milestones-container">
      <h2 className="milestones-title">Marcos Financeiros</h2>
      
      <div className="milestones-description">
        <p>Defina seus objetivos financeiros e descubra quanto tempo levará para alcançá-los com seus investimentos atuais.</p>
        <p>Use esta ferramenta para planejar metas de curto, médio e longo prazo - desde a compra do primeiro carro até sua independência financeira.</p>
      </div>
      
      <div className="milestones-form">
        <div className="input-card">
          <label htmlFor="initialCapital">Capital Inicial (R$)</label>
          <input
            type="number"
            id="initialCapital"
            name="initialCapital"
            value={formData.initialCapital}
            onChange={handleInputChange}
            min="0"
            className="input-field"
          />
          <div className="input-hint">Valor que já possui investido atualmente</div>
        </div>
        
        <div className="input-card">
          <label htmlFor="monthlyContribution">Aporte Mensal (R$)</label>
          <input
            type="number"
            id="monthlyContribution"
            name="monthlyContribution"
            value={formData.monthlyContribution}
            onChange={handleInputChange}
            min="0"
            className="input-field"
          />
          <div className="input-hint">Quanto irá investir todo mês</div>
        </div>
        
        <div className="input-card">
          <label htmlFor="interestRate">Taxa de Juros (% ao mês)</label>
          <input
            type="number"
            id="interestRate"
            name="interestRate"
            value={formData.interestRate}
            onChange={handleInputChange}
            min="0"
            step="0.1"
            className="input-field"
          />
          <div className="input-hint">Rentabilidade mensal esperada dos investimentos</div>
        </div>
      </div>
      
      <div className="target-values">
        <h3 className="section-title">Valores-Alvo</h3>
        <p className="section-description">Selecione os objetivos financeiros que deseja alcançar e defina os valores necessários para cada um.</p>
        
        <div className="target-items-container">
          {targetValues.map((target, index) => (
            <div key={index} className={`target-item ${!target.enabled ? 'disabled' : ''}`}>
              <div className="target-header">
                <div className="target-checkbox-wrapper">
                  <input
                    type="checkbox"
                    id={`target-${index}`}
                    name="enabled"
                    checked={target.enabled}
                    onChange={(e) => handleTargetChange(index, e)}
                    className="custom-checkbox"
                  />
                  <label htmlFor={`target-${index}`} className="checkbox-label">
                    <span className="checkbox-custom"></span>
                    {target.name}
                  </label>
                </div>
                <div className="target-icon">
                  {getTargetIcon(target.name)}
                </div>
              </div>
              <div className="target-inputs">
                <div className="input-group">
                  <label htmlFor={`target-name-${index}`}>Objetivo</label>
                  <input
                    type="text"
                    id={`target-name-${index}`}
                    name="name"
                    value={target.name}
                    onChange={(e) => handleTargetChange(index, e)}
                    disabled={!target.enabled}
                    className="input-field"
                  />
                </div>
                <div className="input-group">
                  <label htmlFor={`target-value-${index}`}>Valor (R$)</label>
                  <input
                    type="number"
                    id={`target-value-${index}`}
                    name="value"
                    value={target.value}
                    onChange={(e) => handleTargetChange(index, e)}
                    disabled={!target.enabled}
                    min="0"
                    className="input-field"
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
        
        <button
          className="add-target-btn"
          onClick={() => {
            setTargetValues([
              ...targetValues,
              { name: 'Novo Objetivo', value: 50000, enabled: true }
            ]);
          }}
        >
          <span role="img" aria-label="Adicionar">➕</span> Adicionar Novo Objetivo
        </button>
      </div>
      
      <button className="calculate-btn" onClick={calculateMilestones}>
        <span role="img" aria-label="Calcular">📊</span> Calcular Tempo para Objetivos
      </button>
      
      {milestonesResults.targets?.length > 0 ? (
        <div className="milestones-results">
          <h3 className="section-title">Resultados dos Marcos</h3>
          
          <div className="chart-container">
            <ResponsiveContainer width="100%" height={300}>
              <LineChart
                data={milestonesResults.chartData}
                margin={{
                  top: 5,
                  right: 30,
                  left: 20,
                  bottom: 5,
                }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="month" 
                  label={{ 
                    value: 'Meses', 
                    position: 'insideBottomRight', 
                    offset: -10 
                  }}
                  tickFormatter={(month) => {
                    if (month % 12 === 0) {
                      return `${month / 12} ${month === 12 ? 'ano' : 'anos'}`;
                    }
                    return '';
                  }}
                />
                <YAxis 
                  tickFormatter={(value) => {
                    if (value >= 1000000) {
                      return `${(value / 1000000).toFixed(1)}M`;
                    } else if (value >= 1000) {
                      return `${(value / 1000).toFixed(0)}K`;
                    }
                    return value;
                  }}
                  label={{ 
                    value: 'Valor Acumulado (R$)', 
                    angle: -90, 
                    position: 'insideLeft' 
                  }}
                />
                <Tooltip 
                  formatter={(value) => [formatCurrency(value), 'Valor Acumulado']}
                  labelFormatter={(month) => {
                    const years = Math.floor(month / 12);
                    const months = month % 12;
                    return `${years} ${years === 1 ? 'ano' : 'anos'} e ${months} ${months === 1 ? 'mês' : 'meses'}`;
                  }}
                />
                <Legend />
                <Line 
                  type="monotone" 
                  dataKey="amount" 
                  stroke="var(--color-primary)" 
                  name="Valor Acumulado" 
                  dot={false}
                  activeDot={{ r: 8 }}
                />
                
                {milestonesResults.targets.map((target, index) => (
                  <ReferenceLine 
                    key={index}
                    y={target.targetValue} 
                    stroke={`hsl(${index * 120}, 70%, 50%)`}
                    strokeDasharray="3 3"
                    label={{ 
                      value: target.name, 
                      position: 'insideTopRight',
                      fill: `hsl(${index * 120}, 70%, 50%)`,
                    }} 
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
          
          <div className="results-grid">
            {milestonesResults.targets.map((target, index) => (
              <div key={index} className="result-card">
                <div className="result-header">
                  <div className="result-icon">
                    {getTargetIcon(target.name)}
                  </div>
                  <div className="result-title">{target.name}</div>
                </div>
                <div className="result-content">
                  <div className="result-item">
                    <div className="result-label">Valor Alvo</div>
                    <div className="result-value">{formatCurrency(target.targetValue)}</div>
                  </div>
                  
                  <div className="result-item">
                    <div className="result-label">Tempo até alcançar</div>
                    <div className="result-value">
                      {target.timeInYears < 1 ? 
                        `${Math.ceil(target.timeInMonths)} meses` : 
                        `${Math.floor(target.timeInYears)} anos e ${Math.ceil(target.timeInMonths % 12)} meses`
                      }
                    </div>
                  </div>
                  
                  <div className="result-item">
                    <div className="result-label">Data estimada</div>
                    <div className="result-value">{formatDate(target.timeInMonths)}</div>
                  </div>
                  
                  <div className="result-item">
                    <div className="result-label">Total investido</div>
                    <div className="result-value">
                      {formatCurrency(formData.initialCapital + (formData.monthlyContribution * target.timeInMonths))}
                    </div>
                  </div>
                  
                  <div className="result-item">
                    <div className="result-label">Rendimentos</div>
                    <div className="result-value">
                      {formatCurrency(target.finalAmount - formData.initialCapital - (formData.monthlyContribution * target.timeInMonths))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          <div className="calculated-info">
            <strong>Dica:</strong> O gráfico acima mostra o crescimento do seu patrimônio ao longo do tempo com as linhas horizontais representando seus objetivos. Quanto mais cedo você começar, mais cedo atingirá seus marcos financeiros!
          </div>
        </div>
      ) : (
        <div className="no-milestones">
          Clique em "Calcular Tempo para Objetivos" para visualizar os resultados
        </div>
      )}
    </div>
  );
};

export default Milestones;